import Prismic from 'prismic-javascript';
import { ApiOptions } from 'prismic-javascript/types/Api';
import { QueryOptions } from 'prismic-javascript/types/ResolvedApi';

import {
  HTTP_SERVER_GATEWAY_TIMEOUT,
  HTTP_CLIENT_ERROR_NOT_FOUND,
} from '../../helpers/http';
import { toDocPrismic, toRegionPrismic } from '../../schema/prismic/mapping';
import { DocumentType } from '../../types/document';
import { REGION } from '../../types/filters';
import {
  normalize,
  normalizeCustomType,
  normalizePaginatedCustomTypes,
} from './normalize';

type PaginatedResult = {
  page: number;
  total_results_size: number;
  total_pages: number;
  next_page?: string;
  prev_page?: string;
  results: any[];
  entities: any;
};

/**
 * Helper function to return a Global Error when an error occured during Master Ref fetching
 */
const newGlobalError = (err: any) => {
  return {
    status: err?.status || HTTP_SERVER_GATEWAY_TIMEOUT,
    payload: err,
  };
};

const normalizeError = async <T>(fn: () => Promise<T>): Promise<T> => {
  try {
    return await fn();
  } catch (err) {
    // eslint-disable-next-line
    console.log(err);
    throw newGlobalError(err);
  }
};
let prismicConfig: ApiOptions = {
  accessToken: process.env.RAZZLE_PRISMIC_ACCESSTOKEN,
};

const getClient = async () => {
  return await Prismic.getApi(
    process.env.RAZZLE_PRISMIC_ENDPOINT,
    prismicConfig
  );
};

export const setPrismicConfig = (config: ApiOptions): void => {
  prismicConfig = {
    ...prismicConfig,
    ...config,
  };
};

/**
 * Get a paginated list of documents by query from prismic
 * */
export const query = async (
  q: string | string[],
  opts: QueryOptions = {}
): Promise<PaginatedResult> =>
  normalizeError(async () => {
    const client = await getClient();
    const response = await client.query(q, opts);
    return normalizePaginatedCustomTypes(response);
  });

/**
 * Get list of documents without pagination from prismic
 * */
export const queryAll = async (
  q: string | string[],
  opts: QueryOptions = {}
): Promise<{
  results: any[];
  entities: any;
}> =>
  normalizeError(async () => {
    const client = await getClient();
    const response = await client.query(q, opts);

    let nextPage = response.next_page ? 2 : 0;
    let prismicDocuments = response.results;
    while (nextPage) {
      const r = await client.query(q, { ...opts, page: nextPage });
      nextPage = r.next_page ? nextPage + 1 : 0;
      prismicDocuments = [...prismicDocuments, ...r.results];
    }

    const normalized = normalize(prismicDocuments);
    return {
      results: normalized.result,
      entities: normalized.entities,
    };
  });

/**
 * Get a document by query from prismic
 */
export const queryFirst = async (
  q: string | string[],
  opts: QueryOptions = {}
): Promise<{
  result: any;
  entities: any;
}> =>
  normalizeError(async () => {
    const client = await getClient();
    const response = await client.queryFirst(q, opts);

    if (!response) {
      throw { status: HTTP_CLIENT_ERROR_NOT_FOUND };
    }

    return normalizeCustomType(response);
  });

/**
 * Get a paginated list of documents by ids from prismic
 */
export const getByIDs = async (
  ids: string[],
  opts: QueryOptions = {}
): Promise<PaginatedResult> =>
  normalizeError(async () => {
    const client = await getClient();
    const response = await client.getByIDs(ids, opts);
    return normalizePaginatedCustomTypes(response);
  });

/**
 * Get a document by id from prismic
 */
export const getByID = async (
  id: string,
  opts: QueryOptions = {}
): Promise<{
  result: any;
  entities: any;
}> =>
  normalizeError(async () => {
    const client = await getClient();
    const response = await client.getByID(id, opts);

    if (!response) {
      throw { status: HTTP_CLIENT_ERROR_NOT_FOUND };
    }

    return normalizeCustomType(response);
  });

/**
 * Get a document by uid from prismic
 */
export const getByUID = async (
  type: string,
  uid: string,
  opts: QueryOptions = {}
): Promise<{
  result: any;
  entities: any;
}> => {
  if (type === DocumentType.HOME) {
    return queryFirst(
      [
        Prismic.Predicates.at('document.type', toDocPrismic(DocumentType.HOME)),
        Prismic.Predicates.at(
          'my.home_page.region',
          toRegionPrismic(uid as REGION)
        ),
      ],
      opts
    );
  }

  return normalizeError(async () => {
    const client = await getClient();
    const response = await client.getByUID(
      toDocPrismic(type as unknown as DocumentType),
      uid,
      opts
    );

    if (!response) {
      throw { status: HTTP_CLIENT_ERROR_NOT_FOUND };
    }

    return normalizeCustomType(response);
  });
};

/**
 * Get a single type document from prismic
 */
export const getSingle = async (
  type: string,
  opts: QueryOptions = {}
): Promise<{
  result: any;
  entities: any;
}> =>
  normalizeError(async () => {
    const client = await getClient();
    const response = await client.getSingle(
      toDocPrismic(type as unknown as DocumentType),
      opts
    );

    if (!response) {
      throw { status: HTTP_CLIENT_ERROR_NOT_FOUND };
    }

    return normalizeCustomType(response);
  });

/**
 * Get a document from preview token
 */
export const getDocForPreview = async (
  token: string,
  documentId: string,
  toUrl: (document: any) => string
): Promise<string> =>
  normalizeError(async () => {
    const client = await getClient();
    return client
      .getPreviewResolver(token, documentId)
      .resolve((prismicDoc) => {
        const { result } = normalizeCustomType(prismicDoc);
        return toUrl(result);
      }, '/');
  });
